// Identifier helpers for turning trackable UI elements into stable event names.
import { PassiveTrackingDom } from 'app/shared/services/fullstory/quote-passive-tracking/quote-passive-tracking.dom';
import { matches } from 'app/shared/utils/dom/dom-helpers';
import { normalizeText, slugify } from 'app/shared/utils/text/text-helpers';

export class PassiveTrackingIdentifiers {
  private static readonly PHONE_IDENTIFIER = 'call-kin';
  private static readonly HEADER_CONTACT_AGENT_IDENTIFIER = 'contact-an-agent';
  private static readonly GENERIC_BOOLEAN_OPTION_IDENTIFIERS = new Set(['yes', 'no', 'true', 'false']);
  private static readonly PHONE_TEST_IDS = ['call-us-button', 'contact-agent-button', 'mobile-call-button', 'phone-number-ejection-mobile', 'phone-number-default'];

  /**
   * Detects a "call Kin" style button: tel: links, known phone-button test ids,
   * labels containing 10+ digits, or common phone-CTA phrases.
   */
  static isPhoneInteraction(element: HTMLElement): boolean {
    const href = (element.getAttribute('href') || '').trim();
    if (/^tel:/i.test(href)) return true;

    const testId = element.getAttribute('data-testid') || '';
    if (PassiveTrackingIdentifiers.PHONE_TEST_IDS.includes(testId)) return true;

    const label = normalizeText(PassiveTrackingDom.getElementLabel(element));
    const digits = label.replace(/\D/g, '');
    if (digits.length >= 10) return true;

    return /(contact an agent|call us|talk to a licensed agent)/i.test(label);
  }

  /** Detects the "Contact an agent" link inside the site header, which gets its own identifier. */
  static isHeaderContactAgentInteraction(element: HTMLElement): boolean {
    if (!element.closest('app-header')) return false;

    const testId = element.getAttribute('data-testid') || '';
    if (testId === 'contact-agent-button') return true;

    const label = normalizeText(PassiveTrackingDom.getElementLabel(element));
    return /contact an agent/i.test(label);
  }

  /**
   * Builds an "<action>-<package>" identifier for the compare-packages page submit buttons
   * (e.g. data-testid="kin-plus-submit-button" + label "View and Customize" → "view-and-customize-kin-plus").
   */
  static getComparePackageButtonIdentifier(element: HTMLElement): string | null {
    const testId = element.getAttribute('data-testid') || '';
    const match = testId.match(/^(.+)-submit-button$/);
    if (!match?.[1]) return null;

    const action = slugify(PassiveTrackingDom.getElementLabel(element));
    const packageIdentifier = slugify(match[1]);
    if (!action || !packageIdentifier) return null;

    return `${action}-${packageIdentifier}`;
  }

  static getRadioOptionLabel(element: HTMLElement): string | null {
    const wrappedTitle = PassiveTrackingDom.getLabelTitleText(element.closest('label'));
    if (wrappedTitle) return wrappedTitle;

    if (matches(element, 'label')) {
      const directTitle = PassiveTrackingDom.getLabelTitleText(element);
      if (directTitle) return directTitle;
    }

    return PassiveTrackingDom.getAssociatedLabelText(element);
  }

  /**
   * Picks a stable identifier for a radio card. For generic "Yes"/"No"/"True"/"False"
   * options it falls back to the radio input's id so we don't collapse every Yes/No
   * question on the page into the same event.
   */
  static getRadioButtonIdentifier(element: HTMLElement): string | null {
    const optionIdentifier = slugify(PassiveTrackingIdentifiers.getRadioOptionLabel(element));
    if (!optionIdentifier) return null;
    if (!PassiveTrackingIdentifiers.GENERIC_BOOLEAN_OPTION_IDENTIFIERS.has(optionIdentifier)) return optionIdentifier;

    const radioInput = element.querySelector(PassiveTrackingDom.RADIO_INPUT_SELECTOR);
    if (!(radioInput instanceof HTMLInputElement)) return optionIdentifier;

    const radioIdentifier = slugify(radioInput.id || radioInput.name);
    return radioIdentifier || optionIdentifier;
  }

  /**
   * Top-level identifier for a button-like element. Tries the special cases in order
   * (header contact-agent, phone CTA, compare-packages, coverage-card summary, radio card)
   * then falls back to the slugified label text.
   */
  static getButtonIdentifier(element: HTMLElement): string | null {
    if (PassiveTrackingIdentifiers.isHeaderContactAgentInteraction(element)) {
      return PassiveTrackingIdentifiers.HEADER_CONTACT_AGENT_IDENTIFIER;
    }

    if (PassiveTrackingIdentifiers.isPhoneInteraction(element)) {
      return PassiveTrackingIdentifiers.PHONE_IDENTIFIER;
    }

    const comparePackageIdentifier = PassiveTrackingIdentifiers.getComparePackageButtonIdentifier(element);
    if (comparePackageIdentifier) {
      return comparePackageIdentifier;
    }

    if (matches(element, 'summary')) {
      const coverageCard = element.closest('app-coverage-card[id]');
      if (coverageCard instanceof HTMLElement && coverageCard.id) {
        return slugify(coverageCard.id);
      }
    }

    if (matches(element, PassiveTrackingDom.RADIO_CARD_SELECTOR)) {
      return PassiveTrackingIdentifiers.getRadioButtonIdentifier(element);
    }

    const label = PassiveTrackingDom.getElementLabel(element);
    return slugify(label);
  }

  /**
   * Top-level identifier for a form field. Uses special casing for the effective-date picker
   * and coverage cards; otherwise slugifies the field's label, aria-label, name, id, or testid.
   */
  static getFieldIdentifier(element: HTMLElement): string | null {
    if (element.tagName.toLowerCase() === 'calendar-date' || element.closest('app-premium-datepicker')) {
      return 'effective-date';
    }

    const coverageCard = element.closest('app-coverage-card[id]');
    if (coverageCard instanceof HTMLElement && coverageCard.id) {
      return slugify(coverageCard.id);
    }

    if (element instanceof HTMLInputElement && element.type === 'checkbox') {
      const checkboxIdentifier = element.id || element.getAttribute('data-testid') || element.name || PassiveTrackingDom.getAssociatedLabelText(element);
      return slugify(checkboxIdentifier);
    }

    const rawIdentifier =
      PassiveTrackingDom.getAssociatedLabelText(element) ||
      element.getAttribute('aria-label') ||
      element.getAttribute('name') ||
      element.getAttribute('id') ||
      element.getAttribute('data-testid') ||
      element.closest('[data-testid]')?.getAttribute('data-testid') ||
      '';

    if (!rawIdentifier) return null;

    return slugify(rawIdentifier);
  }
}
